import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { pollWhileProcessing } from "../lib/postCall";
import { api } from "./client";
import type {
  AdminCallDetail,
  AdminCallRow,
  AdminRelease,
  AdminUser,
  AuditEntry,
  Brand,
  BugFollowUp,
  BugReportDetail,
  BugReportRow,
  ClientVersions,
  ConnectedApps,
  DemoRequest,
  DemoRequestList,
  Invitation,
  InvitationList,
  InviteUser,
  LogEvent,
  LogLevel,
  Me,
  NumberMember,
  Overview,
  Page,
  PatchRelease,
  PatchUser,
  PhoneNumber,
  PolicyDocument,
  PolicyUpdate,
  ProviderConfig,
  ProviderName,
  ProviderTestResult,
  ProviderUpdate,
  PushResult,
  ReleaseList,
  ResolveReports,
  ResolveResult,
  ServerSetting,
  Session,
  UsageReport,
} from "./types";

// Auth

export const auth = {
  me: () => api<Me>("/v1/me"),
  /** `graph_token`: a Microsoft Graph token from the same sign-in, for the member's photo. */
  microsoft: (
    id_token: string,
    device: { name: string; os: string; app_version: string },
    graph_token?: string,
  ) =>
    api<Session>("/v1/auth/microsoft", {
      method: "POST",
      body: { id_token, client: "web", device, ...(graph_token ? { graph_token } : {}) },
    }),
  dev: (email: string) =>
    api<Session>("/v1/auth/dev", {
      method: "POST",
      body: { email, client: "web" },
    }),
  logout: () => api<void>("/v1/auth/logout", { method: "POST" }),
};

// Filters

export interface LogQuery {
  level?: LogLevel | undefined;
  kind?: string | undefined;
  user_id?: string | undefined;
  call_id?: string | undefined;
  since?: string | undefined;
  limit?: number | undefined;
}

export interface CallQuery {
  user_id?: string | undefined;
  from?: string | undefined;
  to?: string | undefined;
  direction?: "inbound" | "outbound" | undefined;
  outcome?: string | undefined;
  dropped?: boolean | undefined;
}

// Endpoints

export const admin = {
  overview: () => api<Overview>("/v1/admin/overview"),
  logs: (q: LogQuery & { cursor?: string | undefined }, signal?: AbortSignal) =>
    api<Page<LogEvent>>("/v1/admin/logs", { query: { ...q }, signal }),
  calls: (q: CallQuery & { cursor?: string | undefined; limit?: number }, signal?: AbortSignal) =>
    api<Page<AdminCallRow>>("/v1/admin/calls", { query: { ...q }, signal }),
  call: (id: string) => api<AdminCallDetail>(`/v1/admin/calls/${encodeURIComponent(id)}`),
  users: () => api<AdminUser[]>("/v1/admin/users"),
  inviteUser: (body: InviteUser) => api<AdminUser>("/v1/admin/users", { method: "POST", body }),
  patchUser: (id: string, body: PatchUser) =>
    api<AdminUser>(`/v1/admin/users/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body,
    }),
  revokeUser: (id: string) =>
    api<void>(`/v1/admin/users/${encodeURIComponent(id)}/revoke`, {
      method: "POST",
    }),
  brands: () => api<Brand[]>("/v1/admin/brands"),
  createBrand: (body: Omit<Brand, "id">) => api<Brand>("/v1/admin/brands", { method: "POST", body }),
  patchBrand: (id: string, body: Partial<Omit<Brand, "id">>) =>
    api<Brand>(`/v1/admin/brands/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body,
    }),
  numbers: () => api<PhoneNumber[]>("/v1/admin/numbers"),
  createNumber: (body: Pick<PhoneNumber, "e164" | "brand_id" | "owner_user_id" | "label">) =>
    api<PhoneNumber>("/v1/admin/numbers", { method: "POST", body }),
  patchNumber: (id: string, body: Partial<Pick<PhoneNumber, "brand_id" | "owner_user_id" | "label">>) =>
    api<PhoneNumber>(`/v1/admin/numbers/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body,
    }),
  numberMembers: (id: string) => api<NumberMember[]>(`/v1/admin/numbers/${encodeURIComponent(id)}/members`),
  addNumberMember: (id: string, userId: string) =>
    api<NumberMember[]>(`/v1/admin/numbers/${encodeURIComponent(id)}/members`, {
      method: "POST",
      body: { user_id: userId },
    }),
  removeNumberMember: (id: string, userId: string) =>
    api<void>(`/v1/admin/numbers/${encodeURIComponent(id)}/members/${encodeURIComponent(userId)}`, {
      method: "DELETE",
    }),
  /** The providers this company owns (ADR 0050): granted kinds only, in display order. */
  providers: () => api<ProviderConfig[]>("/v1/admin/providers"),
  provider: (name: ProviderName) => api<ProviderConfig>(`/v1/admin/providers/${name}`),
  putProvider: (name: ProviderName, body: ProviderUpdate) =>
    api<ProviderConfig>(`/v1/admin/providers/${name}`, { method: "PUT", body }),
  testProvider: (name: ProviderName) =>
    api<ProviderTestResult>(`/v1/admin/providers/${name}/test`, {
      method: "POST",
    }),
  serverSettings: () => api<{ items: ServerSetting[] }>("/v1/admin/server-settings"),
  policies: () => api<PolicyDocument>("/v1/admin/policies"),
  putPolicy: (body: PolicyUpdate) => api<PolicyDocument>("/v1/admin/policies", { method: "PUT", body }),
  usage: (q: { from: string; to: string }) => api<UsageReport>("/v1/admin/usage", { query: q }),
  audit: (q: { cursor?: string | undefined; action?: string | undefined }) =>
    api<Page<AuditEntry>>("/v1/admin/audit", { query: q }),
  bugReports: (q: { cursor?: string | undefined; limit?: number }, signal?: AbortSignal) =>
    api<Page<BugReportRow>>("/v1/admin/bug-reports", { query: q, signal }),
  bugReport: (id: string) => api<BugReportDetail>(`/v1/admin/bug-reports/${encodeURIComponent(id)}`),
  resolveReports: (body: ResolveReports) =>
    api<ResolveResult>("/v1/admin/bug-reports/resolve", { method: "POST", body }),
  messageReporter: (id: string, text: string) =>
    api<BugFollowUp>(`/v1/admin/bug-reports/${encodeURIComponent(id)}/messages`, {
      method: "POST",
      body: { text },
    }),
  reopenReport: (id: string) =>
    api<void>(`/v1/admin/bug-reports/${encodeURIComponent(id)}/reopen`, { method: "POST" }),
  /** Accepted and being worked on; the reporter is told once. */
  markInProgress: (id: string) =>
    api<unknown>(`/v1/admin/bug-reports/${encodeURIComponent(id)}/in-progress`, { method: "POST" }),
  demoRequests: () => api<DemoRequestList>("/v1/admin/demo-requests"),
  patchDemoRequest: (id: string, body: { status: DemoRequest["status"] }) =>
    api<DemoRequest>(`/v1/admin/demo-requests/${encodeURIComponent(id)}`, { method: "PATCH", body }),
  releases: () => api<ReleaseList>("/v1/admin/releases"),
  patchRelease: (id: string, body: PatchRelease) =>
    api<AdminRelease>(`/v1/admin/releases/${encodeURIComponent(id)}`, { method: "PATCH", body }),
  yankRelease: (id: string, reason: string | null) =>
    api<AdminRelease>(`/v1/admin/releases/${encodeURIComponent(id)}/yank`, {
      method: "POST",
      body: reason ? { reason } : {},
    }),
  unyankRelease: (id: string) =>
    api<AdminRelease>(`/v1/admin/releases/${encodeURIComponent(id)}/unyank`, { method: "POST" }),
  clientVersions: () => api<ClientVersions>("/v1/admin/releases/clients"),
  pushRelease: (id: string) =>
    api<PushResult>(`/v1/admin/releases/${encodeURIComponent(id)}/push`, { method: "POST" }),
  connectedApps: () => api<ConnectedApps>("/v1/admin/releases/connected"),
  invitations: () => api<InvitationList>("/v1/admin/invitations"),
  sendInvitation: (userId: string) =>
    api<Invitation>(`/v1/admin/invitations/${encodeURIComponent(userId)}/send`, { method: "POST" }),
};

// Query options

export const qk = {
  me: ["me"] as const,
  overview: ["admin", "overview"] as const,
  logs: (q: LogQuery) => ["admin", "logs", q] as const,
  calls: (q: CallQuery) => ["admin", "calls", q] as const,
  call: (id: string) => ["admin", "call", id] as const,
  users: ["admin", "users"] as const,
  brands: ["admin", "brands"] as const,
  numbers: ["admin", "numbers"] as const,
  providers: ["admin", "providers"] as const,
  provider: (n: ProviderName) => ["admin", "provider", n] as const,
  serverSettings: ["admin", "server-settings"] as const,
  policies: ["admin", "policies"] as const,
  usage: (q: { from: string; to: string }) => ["admin", "usage", q] as const,
  audit: (action?: string) => ["admin", "audit", action ?? ""] as const,
  bugReports: ["admin", "bug-reports"] as const,
  bugReport: (id: string) => ["admin", "bug-report", id] as const,
  demoRequests: ["admin", "demo-requests"] as const,
  releases: ["admin", "releases"] as const,
  clientVersions: ["admin", "releases", "clients"] as const,
  connectedApps: ["admin", "releases", "connected"] as const,
  invitations: ["admin", "invitations"] as const,
};

export const meQuery = queryOptions({
  queryKey: qk.me,
  queryFn: auth.me,
  staleTime: 60_000,
  retry: false,
});
export const overviewQuery = queryOptions({
  queryKey: qk.overview,
  queryFn: admin.overview,
  refetchInterval: 30_000,
});
export const usersQuery = queryOptions({
  queryKey: qk.users,
  queryFn: admin.users,
});
export const brandsQuery = queryOptions({
  queryKey: qk.brands,
  queryFn: admin.brands,
  staleTime: 5 * 60_000,
});
export const numbersQuery = queryOptions({
  queryKey: qk.numbers,
  queryFn: admin.numbers,
});
export const policiesQuery = queryOptions({
  queryKey: qk.policies,
  queryFn: admin.policies,
});
/** The granted providers; the only provider read Integrations makes before a drawer opens. */
export const providersQuery = queryOptions({ queryKey: qk.providers, queryFn: admin.providers });
export const providerQuery = (n: ProviderName) =>
  queryOptions({ queryKey: qk.provider(n), queryFn: () => admin.provider(n) });
export const serverSettingsQuery = queryOptions({
  queryKey: qk.serverSettings,
  queryFn: admin.serverSettings,
  staleTime: 5 * 60_000,
});
export const callQuery = (id: string) =>
  queryOptions({
    queryKey: qk.call(id),
    queryFn: () => admin.call(id),
    refetchInterval: pollWhileProcessing,
  });
export const usageQuery = (q: { from: string; to: string }) =>
  queryOptions({ queryKey: qk.usage(q), queryFn: () => admin.usage(q) });

export const callsInfinite = (q: CallQuery) =>
  infiniteQueryOptions({
    queryKey: qk.calls(q),
    queryFn: ({ pageParam, signal }) => admin.calls({ ...q, cursor: pageParam, limit: 50 }, signal),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });

export const logsInfinite = (q: LogQuery) =>
  infiniteQueryOptions({
    queryKey: qk.logs(q),
    queryFn: ({ pageParam, signal }) => admin.logs({ ...q, cursor: pageParam, limit: 200 }, signal),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });

export const auditInfinite = (action?: string) =>
  infiniteQueryOptions({
    queryKey: qk.audit(action),
    queryFn: ({ pageParam }) => admin.audit({ cursor: pageParam, action }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });

export const bugReportsInfinite = infiniteQueryOptions({
  queryKey: qk.bugReports,
  queryFn: ({ pageParam, signal }) => admin.bugReports({ cursor: pageParam, limit: 50 }, signal),
  initialPageParam: undefined as string | undefined,
  getNextPageParam: (last) => last.next_cursor ?? undefined,
});

// The audio link expires after 5 minutes; refetch before that.
export const bugReportQuery = (id: string) =>
  queryOptions({
    queryKey: qk.bugReport(id),
    queryFn: () => admin.bugReport(id),
    staleTime: 60_000,
    refetchInterval: 4 * 60_000,
  });

export const demoRequestsQuery = queryOptions({
  queryKey: qk.demoRequests,
  queryFn: admin.demoRequests,
  // Platform admins only: a 403 for everyone else is final, not worth retrying.
  retry: false,
});
export const releasesQuery = queryOptions({
  queryKey: qk.releases,
  queryFn: admin.releases,
});
export const clientVersionsQuery = queryOptions({
  queryKey: qk.clientVersions,
  queryFn: admin.clientVersions,
  refetchInterval: 60_000,
});
// Connected apps change as reps restart into a pushed update: poll while the page is open.
export const connectedAppsQuery = queryOptions({
  queryKey: qk.connectedApps,
  queryFn: admin.connectedApps,
  refetchInterval: 10_000,
});
// Invite emails send in a background job; poll while any is still sending.
export const invitationsQuery = queryOptions({
  queryKey: qk.invitations,
  queryFn: admin.invitations,
  refetchInterval: (q) => (q.state.data?.items.some((i) => i.status === "pending") ? 5_000 : false),
});
